import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import get from 'lodash.get';

import { setTriggerTop as setTriggerTopAction } from 'redux/modules/layout';
import { bulkUpdate } from 'redux/modules/navbar';

import Breakpoints from 'lib/Breakpoints';
import loadScript from 'lib/loadScript';
import HeadlinePropType from 'lib/CustomPropTypes/headline';

import MiniPlayerTease from 'components/MiniPlayerTease';

import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { useSingleNav } from 'lib/getABTestStatus';
import { HIDE_GLOBAL_MENU_NAV } from 'lib/brandFeatures.json';
import { withCookies, Cookies } from 'react-cookie';
import { cookieOptionDecider, implodeFeatureFlagValue } from 'lib/cookies';
import { HeaderFallback } from './Fallback';
import { HeaderNavbar } from './Navbar';
import { Sponsor } from './Sponsor';
import { ElectionsNav } from './ElectionsNav';

const mapStateToProps = ({
  navbar,
  article,
}) => ({
  headline: navbar.headline,
  shareUrl: navbar.shareUrl,
  shortcuts: navbar.shortcutsVisible,
  taxonomy: get(article, ['content', 0, 'taxonomy', 'primaryTopic', 'slug'], null),
});

const electionSlug = '2020-election';

const mapActionsToProps = {
  setTriggerTop: setTriggerTopAction,
  updateNavConfig: bulkUpdate,
};

class ServiceHeader extends React.Component {
  static propTypes = {
    adsEnabled: PropTypes.bool,
    currentPath: PropTypes.string,
    header: PropTypes.shape({
      css: PropTypes.shape({}),
      js: PropTypes.shape({
        src: PropTypes.arrayOf(
          PropTypes.string,
        ),
      }),
      html: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }).isRequired,
    headline: HeadlinePropType,
    pageView: PropTypes.string,
    setTriggerTop: PropTypes.func.isRequired,
    shareUrl: PropTypes.string,
    shortcuts: PropTypes.bool.isRequired,
    taxonomy: PropTypes.string,
    updateNavConfig: PropTypes.func,
    vertical: PropTypes.string.isRequired,
    cookies: PropTypes.instanceOf(Cookies).isRequired,
  };

  static defaultProps = {
    adsEnabled: true,
    currentPath: '/',
    headline: null,
    pageView: null,
    shareUrl: null,
    taxonomy: null,
    updateNavConfig: Function.prototype,
  };

  constructor(props) {
    super(props);

    this.state = {
      ready: false,
    };
  }

  componentDidMount() {
    /**
     * TODO: [BENTO-16439] Temporary script to bucket users for singlenav A/B test
     * based on akamai rv. This will be removed after the test is run.
     */
    const {
      cookies,
    } = this.props;

    if (this.hasHeader() && window) {
      const {
        header,
        headline,
        shareUrl,
        shortcuts,
        vertical,
      } = this.props;

      const {
        js: {
          src = [],
        },
      } = header;

      // Set brand/vertical in config object
      if (typeof window.HFSconfig === 'undefined') {
        window.HFSapi = {};
        window.HFSconfig = {
          brand: vertical,
          header: {
            primary: get(headline, 'primary'),
            social: get(headline, 'social'),
            url: this.getSocialShareUrl(shareUrl),
            shortcuts,
          },
        };

        if (!this.hasGlobalNav()) {
          window.HFSconfig.header.globalNav = false;
        }

        if (this.hasSingleNav()) {
          window.HFSconfig.header.useSmallNav = true;
        }
      }

      // Attach scripts
      src.forEach((script, index) => {
        loadScript(script)
          .then(() => {
            // Fire window event to initialize header
            if (index + 1 >= src.length) {
              const event = new CustomEvent('HFS.ready', { detail: 'header' });
              window.dispatchEvent(event);
              // Set ready
              this.setState({ ready: true });
            }
          });
      });

      // Observe height changes
      window.addEventListener('HFS.recalculateHeight', this.recalculateHeight);

      /**
       * TODO: [BENTO-16439] Temporary script to bucket users for singlenav A/B test based
       * on akamai rv. This will be removed after the test is run.
       */
      const hideGlobalNavConf = getFeatureConfigForBrand(HIDE_GLOBAL_MENU_NAV, vertical);
      if (!cookies.get('featureFlag') && get(hideGlobalNavConf, 'bucketPhase', false)) {
        const segment = cookieOptionDecider([['false', 50], ['true', 50]]);
        if (segment) {
          const cookieVal = implodeFeatureFlagValue({ singlenav: segment });
          cookies.set('featureFlag', cookieVal);
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('HFS.recalculateHeight', this.recalculateHeight);
  }

  getSocialShareUrl = (path) => {
    const { vertical } = this.props;
    if (/^\//.test(path)) {
      switch (vertical) {
        case 'today':
          return `https://www.today.com${path}`;
        case 'msnbc':
          return `https://www.msnbc.com${path}`;
        case 'noticias':
          return `https://www.telemundo.com${path}`;
        case 'entretenimiento':
          return `https://www.telemundo.com${path}`;
        case 'shows':
          return `https://www.telemundo.com${path}`;
        case 'telemundo':
          return `https://www.telemundo.com${path}`;
        default:
          return `https://www.nbcnews.com${path}`;
      }
    }
    return path;
  }

  recalculateHeight = () => {
    if (this.wrapperRef && this.wrapperRef.getBoundingClientRect) {
      const { setTriggerTop } = this.props;
      const { top } = this.wrapperRef.getBoundingClientRect();
      setTriggerTop(top + window.pageYOffset);
    }
  }

  hasHeader = () => {
    const { header } = this.props;
    return header && header.html && header.html.length;
  }

  onGlobalInsertShow = () => {
    if (window && window.HFSapi && typeof window.HFSapi.enableGlobalInsert === 'function') {
      // Update stored navbar configuration
      const { updateNavConfig } = this.props;
      updateNavConfig({ hasGlobalInsert: true });
      // Enable global insert via HFS API
      window.HFSapi.enableGlobalInsert();
    }
  }

  onLocalInsertShow = () => {
    if (typeof window === 'object' && window.HFSapi && typeof window.HFSapi.enableLocalInsert === 'function') {
      // Update stored navbar configuration
      const { updateNavConfig } = this.props;
      updateNavConfig({ hasLocalInsert: true, useSmallNav: true });

      // Enable global insert via HFS API
      window.HFSapi.enableLocalInsert();
    }
  }

  insertElectionsNav = () => {
    const { ready } = this.state;
    const { taxonomy } = this.props;
    if (taxonomy === electionSlug) {
      if (ready && this.wrapperRef && this.wrapperRef.querySelector) {
        const container = this.wrapperRef.querySelector('.hfsh');
        const electionsNavContainer = document.createElement('div');
        container.appendChild(electionsNavContainer);

        return ReactDOM.createPortal(
          <ElectionsNav />,
          electionsNavContainer,
        );
      }
    }
    return null;
  }

  /**
   * Test unique case(s) where mini tease appears in non-standard position
   */
  hasTeaseBelowNav = () => {
    const {
      currentPath,
      pageView,
      vertical,
    } = this.props;
    return vertical === 'msnbc' && pageView === 'front' && currentPath === '/' && Breakpoints.isS();
  }

  insertMiniTease = () => {
    const { ready } = this.state;
    let selector = '.js-global-nav-insert';
    let callback = this.onGlobalInsertShow;

    if (ready && this.wrapperRef && this.wrapperRef.querySelector) {
      if (this.hasTeaseBelowNav()) {
        selector = '.js-portal-below-header';
        callback = () => { };
      }

      if (this.hasSingleNav()) {
        selector = '.js-local-nav-insert';
        callback = this.onLocalInsertShow;
      }

      const container = this.wrapperRef.querySelector(selector);
      if (container) {
        return ReactDOM.createPortal(
          <MiniPlayerTease
            useLocalTease={this.hasSingleNav()}
            onShowTease={callback}
          />,
          container,
        );
      }
    }
    return null;
  }

  insertSponsorAd = () => {
    const { ready } = this.state;
    const {
      adsEnabled,
      vertical,
    } = this.props;

    if (ready && this.wrapperRef && this.wrapperRef.querySelector && adsEnabled && vertical !== 'sponsoredcontent') {
      const container = this.wrapperRef.querySelector('.js-sponsor-wrap');
      if (container) {
        return ReactDOM.createPortal(<Sponsor />, container);
      }
    }
    return null;
  }

  // TODO (BENTO-13916): this is brittle, extract in new HFS configuration option
  getProcessedMarkup = (html) => {
    const navHTML = html.match(/<nav\s[^>]+>((?!<\/nav>).*)<\/nav>/);
    if (Array.isArray(navHTML) && navHTML.length >= 2) {
      return navHTML[1];
    }
    return html;
  }

  // TODO (BENTO-13916): this is brittle, extract in new HFS configuration option
  hasConfiguredNavClass = (html, className) => {
    const matchClasses = html.match(/<nav\sclass="([^"]+)"/);
    if (Array.isArray(matchClasses) && matchClasses.length >= 2) {
      return matchClasses[1].split(' ').includes(className);
    }
    return false;
  }

  is2020Elections = () => {
    const { taxonomy } = this.props;
    if (taxonomy !== electionSlug) {
      return false;
    }
    return true;
  }

  hasSingleNav = () => {
    const { vertical, cookies } = this.props;

    // Since the 2020 elections is determined based on taxonomy and not vertical we will
    // use a shortcut here to never show the Single Nav on those pages.
    if (this.is2020Elections()) {
      return false;
    }

    return useSingleNav(vertical, cookies);
  }

  hasGlobalNav = () => {
    const {
      header: {
        html: [html],
      },
    } = this.props;

    if (this.is2020Elections()) {
      return false;
    }

    if (this.hasSingleNav()) {
      return false;
    }

    if (!this.hasConfiguredNavClass(html, 'show-global')) {
      return false;
    }

    return true;
  }

  render() {
    if (!this.hasHeader()) {
      return <HeaderFallback />;
    }

    const {
      header: {
        css: { link: links },
        js: { html: scripts },
        html: [html],
      } = {},
    } = this.props;

    let showPolitics = false;

    if (this.is2020Elections()) {
      showPolitics = true;
    }

    return (
      <>
        <Head>
          {/* Attach stylesheets */}
          {Array.isArray(links) && links.length && links.map((href) => (
            <link key={href} href={href} rel="stylesheet" />
          ))}

          {/* Attach inline scripts */}
          {scripts && Array.isArray(scripts) && scripts.map((src) => (
            <script
              key={src.replace(/\s+/g, '').substr(0, 10)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: decodeURIComponent(src) }}
            />
          ))}
        </Head>

        <>
          <div
            className={classNames('layout-header', { 'show-elections-nav': this.is2020Elections() })}
            id="hfs-header"
            ref={(el) => { this.wrapperRef = el; }}
          >
            <HeaderNavbar
              html={this.getProcessedMarkup(html)}
              showGlobal={this.hasGlobalNav()}
              useSmallNav={this.hasSingleNav()}
              showPolitics={showPolitics}
            />
            <div className="hfsh-spacer" />

            {/* Render tease portal */}
            {this.insertMiniTease()}

            {/* Render sponsorAd portal */}
            {this.insertSponsorAd()}

            {this.insertElectionsNav()}
          </div>

          <div className="js-portal-below-header" />
        </>
      </>
    );
  }
}

export default withCookies(connect(mapStateToProps, mapActionsToProps)(
  ServiceHeader,
));
